import { useEffect, useRef } from 'react';

import { checkFeatureFlagGate } from '@wac-web/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@wac-web/utils/feature-flags/feature-flag-manifest';

import { useFastshiftAnalyticsData, useFireAnalyticsEvents } from '@/analytics';
import { SourceKey, ActionKey, ActionSubjectKey, ActionSubjectIdKey } from '@/analytics/constants';
import { useFastshiftSelector } from '@/state';

import {
	buildDomainWideForYouPageDetailsAttributes,
	buildForYouPageDetailsAttributes,
} from './utils';

import { useSummary } from './';

export const useDashboardPageAnalytics = () => {
	const isOrganizationRedesignEnabled = checkFeatureFlagGate(
		featureFlags.sxpFspOrganizationRedesign,
	);
	const { data: customerDomain } = useFastshiftSelector((state) => state.customerDomain);
	const { data: supportRequests, pending: supportRequestsPending } = useFastshiftSelector(
		(state) => state.supportRequests,
	);

	const domainOrg = useFastshiftSelector((state) => state.selectedDomainOrg);

	const {
		summaryData,
		isL1AssessmentsLoading,
		isMigrationPlanTotalsLoading,
		isInstancesLoading,
		isEmpty: summaryEmpty,
	} = useSummary();

	const summaryLoading =
		isL1AssessmentsLoading || isMigrationPlanTotalsLoading || isInstancesLoading;

	const { identityAttrs } = useFastshiftAnalyticsData();
	const { fireScreenEvent, fireTrackEvent } = useFireAnalyticsEvents(identityAttrs);

	const lastFiredKeyRef = useRef<string | null>(null);

	// Screen event
	useEffect(() => {
		if (!identityAttrs) return;
		fireScreenEvent({
			name: SourceKey.FASTSHIFT_PORTAL_FOR_YOU_SCREEN,
		});
	}, [fireScreenEvent, identityAttrs]);

	// Track event - when the dashboard data required by the active experience is loaded
	useEffect(() => {
		if (!customerDomain || !identityAttrs || !supportRequests || supportRequestsPending) return;

		let attrs;
		if (isOrganizationRedesignEnabled) {
			attrs = buildDomainWideForYouPageDetailsAttributes(supportRequests);
		} else {
			if (!domainOrg || summaryLoading) return;
			attrs = buildForYouPageDetailsAttributes({
				customerDomain,
				domainOrg,
				summaryData,
				summaryEmpty,
				supportRequests,
			});
		}
		if (!attrs) return;

		const key = JSON.stringify({
			...identityAttrs,
			...attrs,
		});

		if (lastFiredKeyRef.current === key) {
			return;
		}

		fireTrackEvent({
			action: ActionKey.DISPLAYED,
			actionSubject: ActionSubjectKey.SECTION,
			actionSubjectId: ActionSubjectIdKey.FOR_YOU_PAGE_DETAILS,
			source: SourceKey.FASTSHIFT_PORTAL_FOR_YOU_SCREEN,
			attributes: attrs,
		});
		lastFiredKeyRef.current = key;
	}, [
		customerDomain,
		domainOrg,
		summaryLoading,
		summaryEmpty,
		summaryData,
		supportRequests,
		supportRequestsPending,
		identityAttrs,
		fireTrackEvent,
		isOrganizationRedesignEnabled,
	]);
};
